import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import { useDispatch } from 'react-redux';

import { importFetchedAccounts } from 'mastodon/actions/importer';
import api from 'mastodon/api';
import { LoadingIndicator } from 'mastodon/components/loading_indicator';

import AutosuggestAccountContainer from '../containers/autosuggest_account_container';

const messages = defineMessages({
  placeholder: { id: 'bot_search.placeholder', defaultMessage: 'Search books and authors…' },
  noResults: { id: 'bot_search.no_results', defaultMessage: 'No matching bots found' },
});

// Same normalization as the knowledge-graph explorer's book search: strip
// Arabic diacritics (harakat) + tatweel and fold alef/ya/ta-marbuta variants
// so e.g. "الجغرافية" matches "الجغرافيا".
const normalize = (s) =>
  s
    .toLowerCase()
    .replace(/[\u064B-\u0652\u0670]/g, '') // harakat + superscript alef
    .replace(/\u0640/g, '') // tatweel
    .replace(/[\u0622\u0623\u0625]/g, '\u0627') // alef variants آأإ → ا
    .replace(/\u0649/g, '\u064A') // alef maqsura ى → ya ي
    .replace(/\u0629/g, '\u0647') // ta marbuta ة → ha ه
    .replace(/\s+/g, ' ')
    .trim();

const stripHtml = (html) =>
  new DOMParser().parseFromString(html || '', 'text/html').documentElement.textContent || '';

// The bot directory is fetched once per session and shared across popup
// instances.
let botsPromise = null;

const fetchBots = () => {
  if (!botsPromise) {
    botsPromise = api().get('/api/v1/bots').then(response => response.data).catch(error => {
      botsPromise = null;
      throw error;
    });
  }

  return botsPromise;
};

const MAX_VISIBLE = 8;

export const BotSearchPopup = ({ onSelect, onClose }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [bots, setBots] = useState(null);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    let cancelled = false;

    fetchBots().then(accounts => {
      if (cancelled) return;

      dispatch(importFetchedAccounts(accounts));

      // Pre-compute normalized haystacks. Display name (Arabic title) and
      // username (English slug) form the primary tier; the bio, which carries
      // the full English/Arabic titles and author names, the secondary one.
      setBots(accounts.map(account => ({
        id: account.id,
        primary: normalize(`${account.display_name} ${account.acct}`),
        full: normalize(`${account.display_name} ${account.acct} ${stripHtml(account.note)}`),
      })));
    }).catch(() => {
      if (!cancelled) setBots([]);
    });

    return () => { cancelled = true; };
  }, [dispatch]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleDocumentMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [onClose]);

  const visible = useMemo(() => {
    if (!bots) return [];

    const needle = normalize(query);
    if (!needle) return bots.slice(0, MAX_VISIBLE);

    const primary = [], secondary = [];

    for (const bot of bots) {
      if (bot.primary.includes(needle)) {
        primary.push(bot);
      } else if (bot.full.includes(needle)) {
        secondary.push(bot);
      }
    }

    return primary.concat(secondary).slice(0, MAX_VISIBLE);
  }, [bots, query]);

  const handleChange = useCallback((e) => {
    setQuery(e.target.value);
    setSelected(0);
  }, []);

  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
    case 'Escape':
      e.preventDefault();
      onClose();
      break;
    case 'ArrowDown':
      e.preventDefault();
      setSelected(s => Math.min(s + 1, visible.length - 1));
      break;
    case 'ArrowUp':
      e.preventDefault();
      setSelected(s => Math.max(s - 1, 0));
      break;
    case 'Enter':
    case 'Tab':
      if (visible.length > 0) {
        e.preventDefault();
        onSelect(visible[Math.min(selected, visible.length - 1)].id);
      }
      break;
    }
  }, [onClose, onSelect, visible, selected]);

  const handleResultMouseDown = useCallback((e) => {
    e.preventDefault();
    onSelect(e.currentTarget.getAttribute('data-id'));
  }, [onSelect]);

  return (
    <div className='bot-search-popup' ref={rootRef}>
      <input
        ref={inputRef}
        type='text'
        dir='auto'
        className='bot-search-popup__input'
        placeholder={intl.formatMessage(messages.placeholder)}
        aria-label={intl.formatMessage(messages.placeholder)}
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      <div className='bot-search-popup__results'>
        {bots === null && <LoadingIndicator />}

        {bots !== null && visible.map((bot, i) => (
          <div role='button' tabIndex={0} key={bot.id} data-id={bot.id} className={classNames('autosuggest-textarea__suggestions__item', { selected: i === selected })} onMouseDown={handleResultMouseDown}>
            <AutosuggestAccountContainer id={bot.id} />
          </div>
        ))}

        {bots !== null && visible.length === 0 && (
          <div className='bot-search-popup__empty'>{intl.formatMessage(messages.noResults)}</div>
        )}
      </div>
    </div>
  );
};

BotSearchPopup.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BotSearchPopup;
